import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  TextInputV2,
  LabraToolTipV2,
  TextAreaV4,
  MultiSelectV4,
} from '@labrav/react-components'
import React, { useCallback } from 'react'

import { faList } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { get } from 'lodash'

import { useStyles } from './FlyOutProductAiTools.styles'

import { DefaultNowType } from '../../../common/utils/dateTimeNow'
import { RequirementLink } from '../../../types/RequirementLink'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductSections } from '../../../types/addEditProduct'

import {
  DataType,
  FormField,
  SelectorOptions,
} from '@labrav/react-components/lib/@types/types/forms'

export type FlyOutProductDetailsFormProps = {
  now: DefaultNowType
  handleRefLinkClick?: (inex: number) => void
  detailView?: boolean
  detailViewTooltip?: string
} & RequirementLink &
  FormLockedType

export const FlyOutProductAiTools: React.FC<FlyOutProductDetailsFormProps> = ({
  isFormLocked,
  detailView,
  detailViewTooltip,
}) => {
  const { classes } = useStyles()

  const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const options: SelectorOptions[] = [
    { value: 'API_KEY', label: 'API Key' },
    { value: 'OAUTH2', label: 'OAuth 2.0' },
  ]
  const apiIntegrationProtocolsOptions: SelectorOptions[] = [
    { value: 'A2A', label: 'A2A' },
    { value: 'MCP', label: 'MCP' },
  ]
  const keyNameAuthorizationMethod = 'generalInformation.authorizationMethod'
  const keyNameApiIntegrationProtocols =
    'generalInformation.apiIntegrationProtocols'
  const authorizationMethod: string[] =
    generalInformation?.authorizationMethod ?? []
  const apiIntegrationProtocolsValue: string[] =
    generalInformation?.apiIntegrationProtocols ?? []
  const handleInputChangeSelect = useCallback(
    (event: unknown, fieldKeyName: string) => {
      const value = (event as React.ChangeEvent<HTMLInputElement>).target?.value
      const selectedValues = Array.isArray(value) ? value : value ? [value] : []
      setFieldValue(fieldKeyName, selectedValues)

      if (fieldKeyName === keyNameApiIntegrationProtocols) {
        const previousValues = (get(values, fieldKeyName, []) ?? []) as string[]

        const removedValues = previousValues.filter(
          item => !selectedValues.includes(item)
        )
        if (removedValues.includes('A2A')) {
          setFieldValue('generalInformation.a2aUsageInstructions', '')
        }
        if (removedValues.includes('MCP')) {
          setFieldValue('generalInformation.mcpUsageInstructions', '')
        }
      }
    },
    [values, setFieldValue, keyNameApiIntegrationProtocols]
  )

  const handleDeleteChip = useCallback(
    (value: string, fieldKeyName: string) => {
      const currentValues = (get(values, fieldKeyName, []) ?? []) as string[]
      const filtered = currentValues.filter(item => item !== value)
      setFieldValue(fieldKeyName, filtered)

      // Clear the corresponding text field when a protocol is deselected
      if (fieldKeyName === keyNameApiIntegrationProtocols) {
        if (value === 'A2A') {
          setFieldValue('generalInformation.a2aUsageInstructions', '')
        } else if (value === 'MCP') {
          setFieldValue('generalInformation.mcpUsageInstructions', '')
        }
      }
    },
    [values, setFieldValue, keyNameApiIntegrationProtocols]
  )

  const multiSelectData: FormField = {
    title: 'Choose options',
    placeHolder: 'Choose options',
    humanReadableKey: 'generalInformation.authorizationMethod',
    id: 'generalInformation.authorizationMethod',
    isReadOnly: false,
    isRequired: true,
    defaultValue: undefined,
    dataType: 'multi_enum' as DataType,
    validationType: undefined,
    isCustom: false,
    options: options,
  }

  const apiIntegrationProtocols: FormField = {
    title: 'Choose options',
    placeHolder: 'Choose options',
    humanReadableKey: 'generalInformation.apiIntegrationProtocols',
    id: 'generalInformation.apiIntegrationProtocols',
    isReadOnly: false,
    isRequired: true,
    defaultValue: undefined,
    dataType: 'multi_enum' as DataType,
    validationType: undefined,
    isCustom: false,
    options: apiIntegrationProtocolsOptions,
  }

  return (
    <BoxV2
      heading={'AI agent'}
      headingIcon={
        <FontAwesomeIcon
          icon={faList}
          style={{ width: '20px', height: '16px' }}
        />
      }
    >
      <>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                API endpoint URL
              </Typography>
            </div>
            <Typography className={classes.sectionText}>
              Enter the URL where your API receives requests. This URL must be
              publicly accessible.
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
            >
              <TextInputV2
                keyName="generalInformation.apiEndpointUrl"
                title="Enter API endpoint URL"
                type="text"
                placeHolder=""
                value={generalInformation?.apiEndpointUrl || ''}
                updatedValue={generalInformation?.apiEndpointUrl || ''}
                isError={
                  !!(
                    generalInformationErrors?.apiEndpointUrl &&
                    generalInformationTouched?.apiEndpointUrl
                  )
                }
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!generalInformationTouched?.apiEndpointUrl &&
                    generalInformationErrors?.apiEndpointUrl) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                viewOnly={detailView}
              />
            </LabraToolTipV2>
          </div>
        </div>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              API usage instructions
            </Typography>
            <Typography className={classes.sectionText}>
              Provide detailed instructions for buyers to use your API such as
              API schema, rate limits, and usage examples. You can also add
              links to your documentation.
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.fullWidth}
          >
            <TextAreaV4
              keyName="generalInformation.apiUsageInstructions"
              title="Enter API usage instructions"
              rows={10}
              maxCharacters={4000}
              height={'100px'}
              type="string"
              placeHolder=""
              value={generalInformation?.apiUsageInstructions || ''}
              updatedValue={generalInformation?.apiUsageInstructions || ''}
              isError={
                !!(
                  generalInformationTouched?.apiUsageInstructions &&
                  generalInformationErrors?.apiUsageInstructions
                )
              }
              onChange={handleChange}
              isReadOnly={isFormLocked as boolean}
              isRequired={false}
              errorText={
                (!!generalInformationTouched?.apiUsageInstructions &&
                  generalInformationErrors?.apiUsageInstructions) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
              viewOnly={detailView}
            />
          </LabraToolTipV2>
        </div>
        {/* multi select */}
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                Authorization method
              </Typography>
            </div>
            <Typography className={classes.sectionText}>
              Choose how customers authenticate and access your API. This
              selection determines the security approach for API requests.
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
            >
              <MultiSelectV4
                field={multiSelectData}
                onChangeSelect={e =>
                  handleInputChangeSelect(e, keyNameAuthorizationMethod)
                }
                selectedValues={authorizationMethod}
                handleDelete={value =>
                  handleDeleteChip(value, keyNameAuthorizationMethod)
                }
                isError={
                  !!(
                    generalInformationTouched?.authorizationMethod &&
                    generalInformationErrors?.authorizationMethod
                  )
                }
                errorText={
                  (!!generalInformationTouched?.authorizationMethod &&
                    generalInformationErrors?.authorizationMethod) ||
                  ''
                }
                onBlur={handleBlur}
              />
            </LabraToolTipV2>
          </div>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                API integration protocols
              </Typography>
              <Typography className={classes.sectionText}>Optional</Typography>
            </div>
            <Typography className={classes.sectionText}>
              Add additional information around how your agent or tools
              communicates with other systems.
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
            >
              <MultiSelectV4
                field={apiIntegrationProtocols}
                onChangeSelect={e =>
                  handleInputChangeSelect(e, keyNameApiIntegrationProtocols)
                }
                selectedValues={apiIntegrationProtocolsValue}
                handleDelete={value =>
                  handleDeleteChip(value, keyNameApiIntegrationProtocols)
                }
                isError={
                  !!(
                    generalInformationTouched?.apiIntegrationProtocols &&
                    generalInformationErrors?.apiIntegrationProtocols
                  )
                }
                errorText={
                  (!!generalInformationTouched?.apiIntegrationProtocols &&
                    generalInformationErrors?.apiIntegrationProtocols) ||
                  ''
                }
                onBlur={handleBlur}
              />
            </LabraToolTipV2>
          </div>
        </div>

        {apiIntegrationProtocolsValue.includes('A2A') && (
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <Typography className={classes.sectionHeading}>
                A2A usage instructions
              </Typography>
              <Typography className={classes.sectionText}>
                Enter instructions for customers to use your product within
                their A2A-enabled system. You can include documentation links.
              </Typography>
            </div>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
              className={classes.fullWidth}
            >
              <TextAreaV4
                keyName="generalInformation.a2aUsageInstructions"
                title="Enter A2A usage instructions"
                rows={10}
                maxCharacters={4000}
                height={'100px'}
                type="string"
                placeHolder=""
                value={generalInformation?.a2aUsageInstructions || ''}
                updatedValue={generalInformation?.a2aUsageInstructions || ''}
                isError={
                  !!(
                    generalInformationTouched?.a2aUsageInstructions &&
                    generalInformationErrors?.a2aUsageInstructions
                  )
                }
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!generalInformationTouched?.a2aUsageInstructions &&
                    generalInformationErrors?.a2aUsageInstructions) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                viewOnly={detailView}
              />
            </LabraToolTipV2>
          </div>
        )}

        {apiIntegrationProtocolsValue.includes('MCP') && (
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <Typography className={classes.sectionHeading}>
                MCP usage instructions
              </Typography>
              <Typography className={classes.sectionText}>
                Enter instructions for customers to use your product within
                their MCP-enabled system. You can include documentation links.
              </Typography>
            </div>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
              className={classes.fullWidth}
            >
              <TextAreaV4
                keyName="generalInformation.mcpUsageInstructions"
                title="Enter MCP usage instructions"
                rows={10}
                maxCharacters={4000}
                height={'100px'}
                type="string"
                placeHolder=""
                value={generalInformation?.mcpUsageInstructions || ''}
                updatedValue={generalInformation?.mcpUsageInstructions || ''}
                isError={
                  !!(
                    generalInformationTouched?.mcpUsageInstructions &&
                    generalInformationErrors?.mcpUsageInstructions
                  )
                }
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!generalInformationTouched?.mcpUsageInstructions &&
                    generalInformationErrors?.mcpUsageInstructions) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                viewOnly={detailView}
              />
            </LabraToolTipV2>
          </div>
        )}
      </>
    </BoxV2>
  )
}
